import { ArrowRight, Users, MessageSquare, Calendar, FileCheck } from "lucide-react";

export const ItStakeholderCollaboration = () => {
  const collaborationTips = [
    {
      icon: Calendar,
      title: "Weekly Leadership Sync",
      description: "CTO, CIO, and Product leaders should meet weekly to align on priorities, blockers, and resource needs. Keep it under 30 minutes.",
      example: "Monday 10am: Review sprint progress, discuss infrastructure needs, align on upcoming releases",
    },
    {
      icon: MessageSquare,
      title: "Clear Communication Channels",
      description: "Define where decisions happen. Strategy in meetings, urgent issues in Slack, documentation in Confluence/Notion.",
      example: "Security incidents → #incident-response Slack channel → CIO paged immediately",
    },
    {
      icon: FileCheck,
      title: "Document Decision Rights",
      description: "Create a decision log. Who approved the cloud provider switch? Who signed off on the security policy? Ambiguity kills velocity.",
      example: "Decision: Migrate to AWS. Accountable: CIO. Approved: 2024-01-15. Rationale: Cost + compliance.",
    },
    {
      icon: Users,
      title: "Cross-Functional Sprints",
      description: "Include DevOps in planning, not just execution. They'll catch infrastructure risks early and prevent last-minute scrambles.",
      example: "Sprint planning includes: PM (what), Engineering (how), DevOps (where/when), Security (risks)",
    },
  ];

  return (
    <section className="py-8 sm:py-12">
      <h2 className="text-2xl sm:text-3xl font-display font-bold text-foreground mb-6">
        Making Stakeholder Collaboration Work
      </h2>
      <p className="text-muted-foreground mb-8 max-w-3xl">
        Defined roles mean nothing without collaboration structures. 
        Here's how high-performing IT organizations keep stakeholders aligned.
      </p>

      <div className="grid md:grid-cols-2 gap-6">
        {collaborationTips.map((tip, index) => (
          <div
            key={index}
            className="bg-card rounded-xl border border-border p-6 hover:shadow-lg transition-shadow"
          >
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary/10 to-indigo-500/10 flex items-center justify-center mb-4">
              <tip.icon className="w-6 h-6 text-primary" />
            </div>
            <h3 className="text-lg font-semibold text-foreground mb-2">{tip.title}</h3>
            <p className="text-sm text-muted-foreground mb-4">{tip.description}</p>
            <div className="text-xs text-muted-foreground bg-muted/50 p-3 rounded-lg">
              <span className="font-medium text-foreground">Example: </span>
              {tip.example}
            </div>
          </div>
        ))}
      </div>

      {/* Common Pitfalls */}
      <div className="mt-10 bg-destructive/5 border border-destructive/20 rounded-xl p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
          <span className="text-destructive">⚠️</span>
          Common Pitfalls to Avoid
        </h3>
        <div className="grid sm:grid-cols-2 gap-4">
          {[
            "CTO and CIO competing instead of collaborating",
            "IT Director bypassed by executives going directly to engineers",
            "Product Manager making technical decisions without CTO input",
            "DevOps treated as 'just deployment' instead of strategic partner",
            "No single source of truth for decisions",
            "Stakeholders meeting too often (meeting fatigue) or too rarely (misalignment)",
          ].map((pitfall, idx) => (
            <div key={idx} className="flex items-start gap-2 text-sm text-muted-foreground">
              <ArrowRight className="w-4 h-4 text-destructive shrink-0 mt-0.5" />
              {pitfall}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
